import type { CSSProperties, ReactElement } from 'react';
import React, { useId } from 'react';
import styled, { css } from 'styled-components';

import { DSTokenMap, FocusOutlineStyle, Spacing } from '@rover/kibble/styles';

const LABEL_HEIGHT = 32;
type LabelProps = {
  isSelected?: boolean;
  disabled?: boolean;
  styleHover?: Record<string, string | undefined>;
};

const getActiveAndHoverStyle: Parameters<typeof styled.label<LabelProps>>[number] = ({
  styleHover,
}) => {
  return css`
    border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_PRESSED.toString()};
    color: ${DSTokenMap.TEXT_COLOR_SECONDARY.toString()};
    ${styleHover && css(styleHover)}
  `;
};

export const Label = styled.label<LabelProps>`
  display: flex;
  height: ${LABEL_HEIGHT}px;
  margin: 0 ${Spacing.S.toString()} ${Spacing.S.toString()} 0;
  border-radius: ${LABEL_HEIGHT / 2}px;
  background: ${(props) =>
    props.disabled
      ? DSTokenMap.BACKGROUND_COLOR_SECONDARY.toString()
      : DSTokenMap.BACKGROUND_COLOR_PRIMARY.toString()};
  font-size: 14px;
  font-weight: 400;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  position: relative;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  padding: 0 ${DSTokenMap.SPACE_4X.toString()};
  border: 2px solid ${DSTokenMap.BORDER_COLOR_PRIMARY.toString()};
  color: ${DSTokenMap.TEXT_COLOR_TERTIARY.toString()};
  &:not(:disabled) {
    @media (hover: hover) {
      &:hover {
        ${getActiveAndHoverStyle}
      }
    }
    &:active {
      ${getActiveAndHoverStyle}
    }
  }

  ${(props) =>
    !props.isSelected
      ? ''
      : css`
          border: 2px solid ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE.toString()};
          color: ${DSTokenMap.INTERACTIVE_TEXT_COLOR_PRIMARY.toString()};
          @media (hover: hover) {
            &:hover {
              ${css`
                /* && makes the styles more specific and higher in priority won't beat inline styles */
                && {
                  ${props.styleHover
                    ? css(props.styleHover)
                    : css`
                        border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE_PRESSED.toString()} !important;
                      `}
                }
              `}
            }
          }
        `}
`;
// Since we want a focus outline on the pill button only when selected via keyboard (not via click),
// we need to use :focus-visible on the checkbox.  However, this doesn't allow us to use :focus-within
// on the "label".  Hence this hacky element which is selectable as a sibling of the checkbox.
const CheckboxFocus = styled.div``;
const HiddenCheckbox = styled.input`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
  &:focus-visible ~ ${CheckboxFocus} {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    ${FocusOutlineStyle({
      outlineOffset: 2,
    })};
  }
`;
export type Props = {
  onClick: (value: string) => void;
  allowSelectMultiple?: boolean;
  inputGroupName?: string;
  isSelected: boolean;
  disabled?: boolean;
  label: string;
  style?: CSSProperties;
  styleHover?: Record<string, string | undefined>;
};

const SelectPill = (props: Props): ReactElement => {
  const {
    onClick,
    allowSelectMultiple = true,
    inputGroupName,
    isSelected = false,
    label,
    disabled,
    style,
    styleHover,
  } = props;
  const pillId = useId();
  return (
    <Label
      isSelected={isSelected}
      disabled={disabled}
      htmlFor={pillId}
      data-testid="styled-label"
      style={style}
      styleHover={styleHover}
    >
      {label}
      <HiddenCheckbox
        type={allowSelectMultiple ? 'checkbox' : 'radio'}
        name={inputGroupName}
        id={pillId}
        checked={isSelected}
        disabled={disabled}
        onChange={() => onClick(label)}
        data-testid="styled-input"
      />
      <CheckboxFocus />
    </Label>
  );
};

export default SelectPill;
